import { Copy, Check } from 'lucide-react'
import React, { useState } from 'react'
import type { ToolCall, ToolResponse } from '@generated/proto/model'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

interface ToolDetailsPopoverProps {
  /** The tool call containing arguments */
  toolCall: ToolCall
  /** The tool response (if completed) */
  toolResponse: ToolResponse | null
  /** Pre-formatted text for copy-to-clipboard */
  copyText: string
  /** The pill content to use as trigger */
  children: React.ReactNode
}

/**
 * A popover that displays detailed tool call parameters and results.
 * Wraps a tool pill and shows details on click.
 */
export function ToolDetailsPopover({
  toolCall,
  toolResponse,
  copyText,
  children,
}: ToolDetailsPopoverProps) {
  const [copied, setCopied] = useState(false)

  // Parse the JSON arguments
  const parameters = React.useMemo(() => {
    try {
      return JSON.parse(toolCall.arguments || '{}')
    } catch {
      return null
    }
  }, [toolCall.arguments])

  // Parse the JSON response content
  const results = React.useMemo(() => {
    if (!toolResponse?.content) return null
    try {
      return JSON.parse(toolResponse.content)
    } catch {
      return null
    }
  }, [toolResponse?.content])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="cursor-pointer">{children}</div>
      </PopoverTrigger>
      <PopoverContent
        className="w-96 max-h-80 overflow-y-auto px-3 py-2"
        side="bottom"
        align="start"
      >
        <div className="space-y-2">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h4 className="font-sans text-sm font-medium text-magazine-charcoal">
              {formatToolName(toolCall.toolName)}
            </h4>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-2 py-1 text-xs text-magazine-ash hover:text-magazine-charcoal hover:bg-magazine-pearl rounded transition-colors"
              title="Copy details"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3" />
                  <span>Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3" />
                  <span>Copy</span>
                </>
              )}
            </button>
          </div>

          {/* Parameters Section */}
          {parameters && Object.keys(parameters).length > 0 && (
            <div>
              <h5 className="font-sans text-xs font-medium text-magazine-ash uppercase tracking-wide mb-1">
                Parameters
              </h5>
              <div className="bg-magazine-pearl/50 rounded-md p-2">
                <KeyValueList data={parameters} />
              </div>
            </div>
          )}

          {/* Results Section */}
          {results && Object.keys(results).length > 0 && (
            <div>
              <h5 className="font-sans text-xs font-medium text-magazine-ash uppercase tracking-wide mb-1">
                Results
              </h5>
              <div className="bg-magazine-pearl/50 rounded-md p-2">
                <KeyValueList data={results} />
              </div>
            </div>
          )}

          {/* Error Section */}
          {toolResponse?.error && (
            <div>
              <h5 className="font-sans text-xs font-medium text-magazine-coral uppercase tracking-wide mb-1">
                Error
              </h5>
              <div className="bg-magazine-coral/10 border border-magazine-coral/20 rounded-md p-2">
                <p className="font-sans text-xs text-magazine-coral">
                  {toolResponse.error}
                </p>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Renders a list of key-value pairs from an object
 */
function KeyValueList({ data }: { data: Record<string, unknown> }) {
  return (
    <dl className="space-y-1">
      {Object.entries(data).map(([key, value]) => (
        <div key={key} className="flex gap-2">
          <dt className="font-sans text-xs text-magazine-ash shrink-0">
            {formatKey(key)}:
          </dt>
          <dd className="font-sans text-xs text-magazine-charcoal break-words min-w-0">
            {formatValue(value)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * Format tool name for display (e.g., "hotel_search" -> "Hotel Search")
 */
function formatToolName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Format key for display (e.g., "checkInDate" -> "Check In Date")
 */
function formatKey(key: string): string {
  // Handle camelCase
  const spaced = key.replace(/([A-Z])/g, ' $1')
  // Handle snake_case
  const snakeSpaced = spaced.replace(/_/g, ' ')
  // Capitalize first letter
  return snakeSpaced.charAt(0).toUpperCase() + snakeSpaced.slice(1).trim()
}

/**
 * Format value for display
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '-'
  }
  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }
  return String(value)
}
